import type { ReactNode } from 'react';
import { Fragment } from 'react';
import type { PascalCase } from 'type-fest';
import type { TieredPlanSkuEnum, TrialablePlanTier, PlanTierEnum } from '~/graphql';
import { Text } from '@wistia/ui';
import { isNotNil } from '~/utilities/type-guards';
import { isNil } from 'lodash';
import { AdPlatformIntegrationIcons, MarketingIntegrationIcons } from './IntegrationIcons';

// #region Enums

export const PlanLevelsEnum = Object.freeze({
  Legacy: -1,
  Free: 1,
  Starter: 2,
  Plus: 2,
  Pro: 3,
  Advanced: 4,
  Premium: 5,
});

// #endregion Enums

// #region Types

export type PlanTier = PlanTierEnum;

export type PlanLevels = (typeof PlanLevelsEnum)[keyof typeof PlanLevelsEnum];

export type DefaultPlanKeys = Capitalize<PlanTier>;
export type PlanKeys = DefaultPlanKeys | 'Advanced2TB' | 'Plus2024';

type TrialPlanNames = PascalCase<TrialablePlanTier>;

export type PurchasePlanTypes = Exclude<PlanTier, 'free' | 'premium'>;

// #endregion Types

// #region Interfaces

export interface TieredPlanLimits {
  archivedMedia: number;
  channels?: number;
  totalMedia: number;
  includedMedia?: number;
  users: number;
}

interface TieredPlan {
  description: string;
  displayName?: string;
  highlights: {
    default: string[];
    redesign?: ReactNode[];
    media?: string[];
    users?: string[];
  };
  level: PlanLevels;
  lostFeatures: Partial<Record<PlanKeys, string[]>>;
  name: string;
  onboardingVideo?: {
    src: string;
    class: string;
  };
  data?: {
    isDiscontinuedOrNonSelfServeTiered: boolean;
    price: {
      annual?: number | null;
      monthly?: number | null;
      custom?: string | null;
    };
    mediaOveragePrice?: number | null;
    limits?: TieredPlanLimits;
  };
  purchasePlanType?: PurchasePlanTypes;
  sku?: TieredPlanSkuEnum;
  titleColorToken: 'brandBlue500' | 'green600' | 'grey900' | 'pink600' | 'purple600';
  trialablePlanTier?: TrialablePlanTier;
  tier: PlanTier;
  isEnabled?: (
    enrollment: { key: string; group?: string | null | undefined } | null | undefined,
  ) => boolean;
}
interface TrialCopy {
  footer?: string;
  list: {
    id: number;
    text: string;
    level?: (typeof PlanLevelsEnum)[keyof typeof PlanLevelsEnum];
  }[];
}

// #endregion Interfaces

// #region Tiered Plans

const TIERED_PLANS: Record<PlanKeys, TieredPlan> = {
  Free: {
    description: 'Get started with Wistia for free',
    highlights: {
      default: ['Up to 10 videos', 'Customizable player', 'Basic analytics'],
    },
    data: {
      isDiscontinuedOrNonSelfServeTiered: false,
      price: {
        annual: 0,
        monthly: 0,
      },
      limits: {
        totalMedia: 10,
        archivedMedia: 0,
        users: 1,
        channels: 0,
      },
    },
    name: 'Free',
    level: PlanLevelsEnum.Free,
    sku: 'free_2022_tiered',
    titleColorToken: 'grey900',
    lostFeatures: {
      Plus: [
        '2 additional users',
        'Removing Wistia branding from video player and media page',
        'Email integrations, like Mailchimp, Keep, and Drip',
        'In-video calls to action (CTAs) for audience engagement',
      ],
      Pro: [
        'Customizations like lead capture forms and password protection',
        'Channels for easy, embeddable video galleries',
        'Enhanced exportable analytics to gain insight into performance by embed location and more',
      ],
      Advanced: [
        'Marketing automation integrations',
        'Wistia Live for seamless webinar hosting',
        'Unlimited Channels to get more eyes on your content',
        'Customizations like lead capture forms and password protection',
        'Enhanced exportable analytics to gain insight into performance by embed location and more',
      ],
    },
    tier: 'free',
  },
  Starter: {
    description: 'Brand & share videos',
    highlights: {
      default: ['Remove Wistia logo', 'More media storage', 'Expiring links'],
      redesign: ['Remove Wistia logo', 'More media storage', 'Expiring links'],
      users: [],
    },
    data: {
      isDiscontinuedOrNonSelfServeTiered: false,
      price: {
        annual: 10,
        monthly: 13,
      },
      limits: {
        totalMedia: 75,
        includedMedia: 20,
        archivedMedia: 100,
        users: 1,
        channels: 3,
      },
    },
    name: 'Starter',
    level: PlanLevelsEnum.Starter,
    sku: 'starter_2025_august_tiered',
    titleColorToken: 'green600',
    lostFeatures: {
      Pro: [
        'Customizations like lead capture forms and password protection',
        'Channels for easy, embeddable video galleries',
        'Enhanced exportable analytics to gain insight into performance by embed location and more',
      ],
      Advanced: [
        'Marketing automation integrations',
        'Wistia Live for seamless webinar hosting',
        'Unlimited Channels to get more eyes on your content and more',
        'Customizations like lead capture forms and password protection',
        'Enhanced exportable analytics to gain insight into performance by embed location and more',
      ],
    },
    tier: 'starter',
    purchasePlanType: 'starter',
    trialablePlanTier: 'STARTER',
    isEnabled: (enrollment) =>
      isNotNil(enrollment) &&
      enrollment.key === 'starter_august2025' &&
      enrollment.group === 'test',
  },
  Plus: {
    description: 'Make your videos look more professional with access to essential features',
    highlights: {
      default: ['Remove Wistia branding', 'Basic CTAs', 'Multiple users'],
      redesign: [
        'Fully customizable player',
        'Clickable video CTAs',
        'Team commenting and reviews',
      ],
      users: ['Up to 3 users', 'Lead capture forms', 'Email integrations'],
    },
    data: {
      isDiscontinuedOrNonSelfServeTiered: true,
      mediaOveragePrice: 2.0,
      price: {
        annual: 19,
        monthly: 24,
      },
      limits: {
        totalMedia: 75,
        includedMedia: 20,
        archivedMedia: 100,
        users: 3,
        channels: 3,
      },
    },
    name: 'Plus',
    displayName: 'Plus Plan 2022',
    level: PlanLevelsEnum.Plus,
    sku: 'plus_2022_tiered',
    titleColorToken: 'green600',
    lostFeatures: {
      Pro: [
        'Custom HTML and image calls-to-action',
        'Password protection',
        'Exporting analytics data for reporting',
        'Live chat and priority support, and more',
      ],
      Advanced: [
        'Marketing automation integrations',
        'Reach & Retarget for creating custom advertising audience',
        'Unlimited Channels to get more eyes on your content',
        'Player customizations like Turnstile and password protection',
        'Deeper analytics, and more',
      ],
    },
    onboardingVideo: {
      src: 'https://fast.wistia.com/embed/medias/89jmcy3mks.jsonp',
      class: 'wistia_async_89jmcy3mks',
    },
    purchasePlanType: 'plus',
    trialablePlanTier: 'PLUS',
    tier: 'plus',
  },
  Plus2024: {
    description: 'Unlock custom video branding',
    highlights: {
      default: ['Remove Wistia branding', 'Basic CTAs', 'Multiple users'],
      redesign: [
        'Fully customizable player',
        'Clickable video CTAs',
        'Team commenting and reviews',
      ],
      users: ['Up to 3 users', 'Lead capture forms', 'Email integrations'],
    },
    data: {
      isDiscontinuedOrNonSelfServeTiered: false,
      mediaOveragePrice: 2.0,
      price: {
        annual: 19,
        monthly: 24,
      },
      limits: {
        totalMedia: 75,
        includedMedia: 20,
        archivedMedia: 100,
        users: 3,
        channels: 0,
      },
    },
    name: 'Plus',
    level: PlanLevelsEnum.Plus,
    sku: 'plus_2024_tiered',
    titleColorToken: 'green600',
    lostFeatures: {
      Pro: [
        'Customizations like lead capture forms and password protection',
        'Channels for easy, embeddable video galleries',
        'Enhanced exportable analytics to gain insight into performance by embed location and more',
      ],
      Advanced: [
        'Marketing automation integrations',
        'Wistia Live for seamless webinar hosting',
        'Unlimited Channels to get more eyes on your content and more',
        'Customizations like lead capture forms and password protection',
        'Enhanced exportable analytics to gain insight into performance by embed location and more',
      ],
    },
    onboardingVideo: {
      src: 'https://fast.wistia.com/embed/medias/89jmcy3mks.jsonp',
      class: 'wistia_async_89jmcy3mks',
    },
    purchasePlanType: 'plus',
    trialablePlanTier: 'PLUS',
    tier: 'plus',
    isEnabled: (enrollment) =>
      isNil(enrollment) ||
      enrollment.key !== 'starter_august2025' ||
      isNil(enrollment.group) ||
      enrollment.group === 'control',
  },
  Pro: {
    description: 'Start generating leads from video',
    highlights: {
      default: ['Lead capture forms', 'Enhanced data analytics', 'Embeddable playlists'],
      redesign: [
        'Heatmaps and audience analytics',
        'In-video lead capture forms',
        'Embeddable playlists',
        'Password-protected videos',
      ],
      users: ['Up to 5 users', 'Video heatmaps', 'Custom CTAs'],
    },
    data: {
      isDiscontinuedOrNonSelfServeTiered: false,
      mediaOveragePrice: 1.0,
      price: {
        annual: 79,
        monthly: 99,
      },
      limits: {
        totalMedia: 500,
        includedMedia: 50,
        archivedMedia: 500,
        users: 5,
        channels: 5,
      },
    },
    name: 'Pro',
    level: PlanLevelsEnum.Pro,
    sku: 'pro_2022_tiered',
    titleColorToken: 'brandBlue500',
    lostFeatures: {
      Advanced: [
        'Webinars',
        'Marketing automation integrations',
        'Unlimited Channels to get more eyes on your content and more',
      ],
    },
    purchasePlanType: 'pro',
    trialablePlanTier: 'PRO',
    tier: 'pro',
  },
  Advanced: {
    description: 'Optimize your video marketing',
    highlights: {
      default: ['Marketing integrations', 'Unlimited video galleries', 'Custom branded webinars'],
      redesign: [
        'Webinars with custom branding',
        <Fragment key="Marketing integrations">
          <Text>Marketing integrations</Text>
          <MarketingIntegrationIcons />
        </Fragment>,
        'Automate campaigns with video',
        <Fragment key="Ad platform integrations">
          Ad platform integrations <AdPlatformIntegrationIcons />
        </Fragment>,
        'Account success manager',
      ],
      users: ['Up to 20 users', 'Marketing integrations', 'Wistia Live'],
    },
    data: {
      isDiscontinuedOrNonSelfServeTiered: false,
      mediaOveragePrice: 0.5,
      price: {
        annual: 319,
        monthly: 399,
      },
      limits: {
        includedMedia: 250,
        totalMedia: 2000,
        archivedMedia: 2500,
        users: 20,
      },
    },
    name: 'Advanced',
    level: PlanLevelsEnum.Advanced,
    sku: 'advanced_2022_tiered',
    titleColorToken: 'purple600',
    lostFeatures: {},
    purchasePlanType: 'advanced',
    trialablePlanTier: 'ADVANCED',
    tier: 'advanced',
  },
  Advanced2TB: {
    description: 'Optimize your video strategy and control the customer experience—start to finish',
    highlights: {
      default: ['Marketing integrations', 'Unlimited video galleries', 'Custom branded webinars'],

      users: ['Up to 30 users', 'Marketing integrations', 'Wistia Live'],
    },
    data: {
      isDiscontinuedOrNonSelfServeTiered: true,
      price: {
        annual: 479,
        monthly: 599,
      },
      limits: {
        includedMedia: 250,
        totalMedia: 2000,
        archivedMedia: 2500,
        users: 30,
      },
      mediaOveragePrice: 0.5,
    },
    name: 'Advanced',
    displayName: 'Advanced Plan 2TB',
    level: PlanLevelsEnum.Advanced,
    sku: 'advanced_2024_tiered_2tb',
    titleColorToken: 'purple600',
    lostFeatures: {},
    purchasePlanType: 'advanced',
    trialablePlanTier: 'ADVANCED',
    tier: 'advanced',
  },
  Premium: {
    description: 'Expand your video strategy with even more content and collaborators',
    highlights: {
      default: ['Unlimited users', 'Unlimited media', 'Single Sign-On'],
    },
    data: {
      isDiscontinuedOrNonSelfServeTiered: false,
      price: {
        annual: undefined,
        monthly: undefined,
        custom: 'Get in touch',
      },
    },
    name: 'Premium',
    level: PlanLevelsEnum.Premium,
    sku: undefined,
    titleColorToken: 'pink600',
    lostFeatures: {},
    tier: 'premium',
  },
};

const UPGRADE_PURCHASE_TYPE_BY_LEVEL: Record<number, PurchasePlanTypes | undefined> = {
  [PlanLevelsEnum.Free]: 'plus',
  [PlanLevelsEnum.Plus]: 'pro',
  [PlanLevelsEnum.Pro]: 'advanced',
  [PlanLevelsEnum.Advanced]: undefined,
  [PlanLevelsEnum.Premium]: undefined,
};

const OUTDATED_OR_NONSTANDARD_TIERED_SKUS: string[] = [
  'plus_2022_tiered',
  'advanced_2024_tiered_2tb',
];

// #endregion Tiered Plans

// #region Trials Copy

// Trials copy, we need to show different copy based on the plan users want to try,
// therefore, the UI filters the features based on the user current plan's level
const wistiaBrand = 'Remove the Wistia branding from the player and media page';
const unlockMore = 'Unlock more analytics and reporting capabilities';
const integrations = 'Get insights by integrating with HubSpot, Pardot, or Marketo';
const audiences = 'Create advertising audiences based on viewing behavior';
const automation = 'Automate your work with email marketing integrations';
const moreUsers = 'Add more users to collaborate with your team';
const videoLibrary = 'Expand your video library';
const inVideoCTA = 'Drive viewer engagement with in-video Calls-to-Action';

const TIERED_TRIALS_COPY: Record<TrialPlanNames, TrialCopy> = {
  Advanced: {
    list: [
      {
        id: 1,
        text: 'Capture leads and seamlessly pass them to your marketing automation platform (Hubspot, Marketo, or Pardot) or email provider',
        level: PlanLevelsEnum.Advanced,
      },
      {
        id: 2,
        text: 'Host beautiful, branded webinars AND leverage the effortless recording - all within Wistia',
        level: PlanLevelsEnum.Advanced,
      },
      {
        id: 3,
        text: 'Up to 20 users for broader team collaboration',
        level: PlanLevelsEnum.Advanced,
      },
      {
        id: 4,
        text: "Retarget audiences who've engaged with your videos",
        level: PlanLevelsEnum.Advanced,
      },
      {
        id: 5,
        text: 'All of our advanced analytics tools to help you optimize your video marketing',
        level: PlanLevelsEnum.Advanced,
      },
    ],
  },
  Pro: {
    list: [
      {
        id: 1,
        text: 'Advanced analytics like performance by embedded location',
        level: PlanLevelsEnum.Pro,
      },
      {
        id: 2,
        text: 'Capture leads and gate content with in-video forms',
        level: PlanLevelsEnum.Pro,
      },
      { id: 3, text: 'Additional users to collaborate with your team', level: PlanLevelsEnum.Pro },
      {
        id: 4,
        text: 'Channels, our easy and beautiful embeddable video galleries to engage your audience and grow subscribers',
        level: PlanLevelsEnum.Pro,
      },
      { id: 5, text: inVideoCTA, level: PlanLevelsEnum.Pro },
    ],
  },
  Plus: {
    list: [
      {
        id: 1,
        text: 'Remove Wistia branding from your video player and media page',
        level: PlanLevelsEnum.Plus,
      },
      { id: 2, text: 'Add 2 additional users for team collaboration', level: PlanLevelsEnum.Plus },
      {
        id: 3,
        text: 'Connect email integrations like Mailchimp, ActiveCampaign, and more to easily include video thumbnails in email campaigns',
        level: PlanLevelsEnum.Plus,
      },
      { id: 4, text: inVideoCTA, level: PlanLevelsEnum.Plus },
    ],
  },
  Starter: {
    list: [],
  },
};

const TIERED_DURING_TRIALS_COPY: Record<TrialPlanNames, TrialCopy> = {
  Advanced: {
    list: [
      {
        id: 1,
        text: videoLibrary,
      },
      {
        id: 2,
        text: integrations,
      },
      {
        id: 3,
        text: 'Host webinars right in Wistia',
      },
      {
        id: 4,
        text: moreUsers,
      },
      {
        id: 5,
        text: audiences,
      },
    ],
  },
  Pro: {
    list: [
      { id: 1, text: videoLibrary },
      {
        id: 2,
        text: 'Host webinars right in Wistia',
      },
      { id: 3, text: 'Gate your content with lead capture forms or password protection' },
      { id: 4, text: unlockMore },
      { id: 5, text: moreUsers },
      { id: 6, text: 'Create channels for a no-code gallery experience' },
    ],
  },
  Plus: {
    list: [
      { id: 1, text: videoLibrary },
      { id: 2, text: wistiaBrand },
      { id: 3, text: moreUsers },
      { id: 4, text: automation },
      {
        id: 5,
        text: 'Drive engagement with in-video Calls to Action',
      },
    ],
  },
  Starter: {
    list: [
      { id: 1, text: videoLibrary },
      { id: 2, text: wistiaBrand },
      { id: 3, text: 'Archive media to organize and save space' },
    ],
  },
};

const TRIALS_COPY_INTERSTITIAL_LOST_FEATURES: Record<TrialablePlanTier, string[]> = {
  STARTER: [
    'Removing the Wistia branding from the player',
    'More media included for your growing content library',
    'Custom share links with expiration',
  ],
  PLUS: [
    '2 additional users to collaborate with your team',
    'Removing Wistia branding',
    'Access to email integrations like Mailchimp, Keap, and Drip',
    'Audience engagement features like Calls to Action',
    'Branded media page customizations',
  ],
  PRO: [
    'Additional users',
    'In-video lead capture forms',
    'Password protected videos',
    'Playlists and channels',
    'Enhanced analytics like embedded location, traffic referrals, and heatmaps',
  ],
  ADVANCED: [
    'Collaboration with up to 20 users',
    'Integrations with marketing automation platforms like Hubspot, Marketo, and Pardot',
    'Wistia Live for seamless webinar hosting',
    'Unlimited channels for easy, embeddable video galleries and more',
  ],
};

// #endregion Trials Copy

// #region Helper Methods

const isLegacyPlan = (
  plan: Record<string, unknown> | TieredPlan,
): plan is Record<string, unknown> => {
  return plan.level == null;
};

const getAvailablePlansCopy = (plans: TieredPlan[]): string => {
  if (plans.length === 0) {
    return '';
  }

  if (plans.length === 1) {
    return plans[0].name;
  }

  if (plans.length === 2) {
    return `${plans[0].name} or ${plans[1].name}`;
  }

  return plans
    .map((plan) => plan.name)
    .reduce((name, value, i, array) => name + (i < array.length - 1 ? ', ' : ', or ') + value);
};

const getDefaultPlansFilter = (currentPlan?: TieredPlan) => {
  return (plan: TieredPlan): boolean => {
    const filteredLevel = plan.level;

    // If current plan at runtime isn't tiered or is null, show self-serve paid plans by default.
    if (currentPlan == null || isLegacyPlan(currentPlan)) {
      return filteredLevel > TIERED_PLANS.Free.level && filteredLevel < TIERED_PLANS.Premium.level;
    }

    const currentLevel = currentPlan.level;

    // For free, show the next three plans.
    if (currentLevel === TIERED_PLANS.Free.level) {
      return filteredLevel > currentLevel && filteredLevel <= currentLevel + 3;
    }

    // All others, show the next two.
    return filteredLevel === currentLevel + 1 || filteredLevel === currentLevel + 2;
  };
};

// #endregion Helper Methods

// #region Exports

export type { TieredPlan };
export {
  getAvailablePlansCopy,
  getDefaultPlansFilter,
  TIERED_DURING_TRIALS_COPY,
  TIERED_PLANS,
  TIERED_TRIALS_COPY,
  TRIALS_COPY_INTERSTITIAL_LOST_FEATURES,
  UPGRADE_PURCHASE_TYPE_BY_LEVEL,
  OUTDATED_OR_NONSTANDARD_TIERED_SKUS,
};

// #endregion Exports
